import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sidebar", "thread", "title", "status", "input", "sendButton", "sendStatus", "twinComposer", "list", "history", "historyToggle", "empty"]
  static values = { chatUrl: String, newChatUrl: String, ownerName: String, canChat: Boolean, profileSlug: String }

  connect() {
    this._onCallEnded = (event) => {
      if (event.detail?.profileSlug !== this.profileSlugValue) return
      this._twinChatId = String(event.detail.twinChatId)
      const url = new URL(this.chatUrlValue, window.location.origin)
      url.searchParams.set("conversation_id", this._twinChatId)
      this._twinChatUrl = url.toString()
      const row = this.element.querySelector(`.profile-message-row[data-chat-id="${this._twinChatId}"]`)
      if (!row) this._addTwinChatRow({ id: this._twinChatId, title: "Voice conversation" })
      if (this._chatOpen) {
        this._activate(this.element.querySelector(`.profile-message-row[data-chat-id="${this._twinChatId}"]`))
        this._personThreadUrl = null
        this.twinComposerTarget.classList.remove("hidden")
        this._loadTwinChat().catch(() => this._showLoading("Couldn't load this conversation. Try again."))
      }
    }
    window.addEventListener("profile-voice:ended", this._onCallEnded)
    // The panel closing (its X, Escape, the Messages sidebar taking over).
    this._onPanelClosed = () => this._reset()
    window.addEventListener("profile-chat:closed", this._onPanelClosed)
    this._onTabHistory = (event) => {
      if (event.detail?.tab === "messages") this.selectMessagesTab()
      else this.close()
    }
    this.element.addEventListener("profile-navigation:history", this._onTabHistory)
    this._selectMessagesOnFrame = requestAnimationFrame(() => {
      if (this.element.matches("[data-profile-navigation-active-value=messages]")) this.selectMessagesTab()
    })
  }

  disconnect() {
    window.removeEventListener("profile-voice:ended", this._onCallEnded)
    window.removeEventListener("profile-chat:closed", this._onPanelClosed)
    this.element.removeEventListener("profile-navigation:history", this._onTabHistory)
    cancelAnimationFrame(this._selectMessagesOnFrame)
  }

  // Message, the avatar's Chat, Connect, /u/:slug/messages: open the panel on
  // the latest conversation - the direct thread first when it asks for the
  // person (Message on a connection's page) - or the starters.
  selectMessagesTab(event) {
    event?.preventDefault?.()
    this._openSidebar()
    const wantsPerson = event?.params?.person || new URL(window.location.href).searchParams.has("person")
    const firstRow = this.hasListTarget && this.listTarget.querySelector(".profile-message-row")
    const personRow = wantsPerson && this.hasListTarget && this.listTarget.querySelector("a.profile-message-row")
    if (personRow || firstRow) (personRow || firstRow).click()
    else this._showEmptyState()
  }

  close() {
    this._chatPanel()?.close()
    this._reset()
  }

  _reset() {
    this._chatOpen = false
    this.sidebarTarget.hidden = true
    this._personThreadUrl = null
    this._twinChatId = null
    if (this.hasHistoryTarget) this.historyTarget.hidden = true
    if (this.hasHistoryToggleTarget) this.historyToggleTarget.setAttribute("aria-expanded", "false")
  }

  toggleHistory() {
    if (!this._chatOpen) this.selectMessagesTab()
    const open = this.historyTarget.hidden
    this.historyTarget.hidden = !open
    this.sidebarTarget.hidden = open
    this.historyToggleTarget.setAttribute("aria-expanded", String(open))
    this.titleTarget.textContent = open ? "Chat history" : (this._chatTitle || "Chat")
    if (open) this.historyTarget.querySelector(".profile-message-row, .profile-new-chat-card")?.focus()
    else this._historyMenuButton().focus()
  }

  // The history line lives in the header's menu, which is closed once the
  // history is: focus returns to the menu's button, not the hidden line.
  _historyMenuButton() {
    return this.historyToggleTarget.closest(".messages-view-menu")?.querySelector("[data-split-menu-target='caret']") || this.historyToggleTarget
  }

  talk(event) {
    event.preventDefault()
    if (!this.canChatValue) return
    window.dispatchEvent(new CustomEvent("assistant-voice:open", { detail: {
      profileSlug: this.profileSlugValue, twinChatId: this._twinChatId
    } }))
  }

  openTwin(event) {
    const host = this.element.querySelector("[data-controller~='profile-stage']")
    this.application.getControllerForElementAndIdentifier(host, "profile-stage")?.openStage(event)
  }

  async openPersonThread(event) {
    event.preventDefault()
    const link = event.currentTarget
    this._activate(link)
    this._personThreadUrl = link.href
    this._twinChatId = null
    this.twinComposerTarget.classList.add("hidden")
    this._setTitle(link.dataset.threadTitle)
    this._showLoading("Loading conversation…")

    try {
      const response = await fetch(this._personThreadUrl, {
        headers: { Accept: "text/html" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`Conversation load failed (${response.status})`)
      this.threadTarget.innerHTML = await response.text()
      const empty = this.threadTarget.querySelector(".connection-chat-empty")
      if (empty) {
        const starters = this.emptyTarget.content.cloneNode(true)
        starters.querySelector("h2").textContent = `Chat with ${this.ownerNameValue}`
        empty.replaceWith(starters)
      }
      this._scrollToEnd()
    } catch (error) {
      console.error(error)
      this._showLoading("Couldn't load this conversation. Try again.")
    }
  }

  async openTwinChat(event) {
    event.preventDefault()
    const row = event.currentTarget
    this._activate(row)
    this._personThreadUrl = null
    this._twinChatId = row.dataset.chatId
    this._twinChatUrl = row.dataset.chatUrl
    this._setTitle(row.dataset.threadTitle)
    this.twinComposerTarget.action = this.chatUrlValue
    this.twinComposerTarget.classList.remove("hidden")
    this.sendStatusTarget.textContent = ""
    this._showLoading("Loading conversation…")
    try {
      await this._loadTwinChat()
    } catch (error) {
      console.error(error)
      this._showLoading("Couldn't load this conversation. Try again.")
    }
  }

  async startTwinChat(event) {
    event.preventDefault()
    if (!this.canChatValue || this._creatingChat) return
    this._creatingChat = true
    this._openChatPanel()
    const prompt = event.currentTarget.dataset.prompt || ""
    this._openSidebar()
    this._setTitle("New conversation")
    this._showLoading("Starting a new chat…")

    try {
      const response = await fetch(this.newChatUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
          "Content-Type": "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify({ answer_id: event.currentTarget.dataset.answerId || null })
      })
      if (!response.ok) throw new Error(`New conversation failed (${response.status})`)
      const chat = await response.json()
      this._twinChatId = String(chat.id)
      this._twinChatUrl = new URL(this.chatUrlValue, window.location.href).toString()
      const url = new URL(this._twinChatUrl)
      url.searchParams.set("conversation_id", this._twinChatId)
      this._twinChatUrl = url.toString()
      this._personThreadUrl = null
      this.twinComposerTarget.action = this.chatUrlValue
      this.twinComposerTarget.classList.remove("hidden")
      this._setTitle(chat.title || "New conversation")
      this._addTwinChatRow(chat)
      await this._loadTwinChat()
      if (prompt) {
        this.inputTarget.value = prompt
        this.inputTarget.focus()
      }
    } catch (error) {
      console.error(error)
      this._showLoading("Couldn't start a new chat. Try again.")
    } finally {
      this._creatingChat = false
    }
  }

  resizeInput() {
    const field = this.inputTarget
    field.style.height = "auto"
    field.style.height = `${Math.min(field.scrollHeight, 200)}px`
  }

  async sendTwinMessage(event) {
    event.preventDefault()
    const body = this.inputTarget.value.trim()
    if (!body || this._sendingChat) return
    this._sendingChat = true
    this.sendButtonTarget.disabled = true
    this._chatLine("asker", body)
    this.inputTarget.value = ""
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.sendStatusTarget.textContent = "Thinking…"
    const requestedThreadId = this._twinChatId

    try {
      const response = await fetch(this.chatUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        body: JSON.stringify({ body, conversation_id: this._twinChatId || "", async: true }),
        credentials: "same-origin"
      })
      let data = await response.json()
      if (!response.ok || !data.ok) throw new Error(data.error || `Message send failed (${response.status})`)
      if (data.pending) {
        const invocationId = data.invocation_id
        const deadline = Date.now() + 5 * 60 * 1000
        do {
          if (Date.now() >= deadline) throw new Error("This is still running. Check this conversation shortly.")
          await new Promise((resolve) => setTimeout(resolve, 1000))
          const status = await fetch(`/assistant/voice_tool_status?invocation_id=${invocationId}`, { headers: { Accept: "application/json" } })
          if (!status.ok) throw new Error("Couldn't retrieve the reply")
          data = await status.json()
        } while (data.pending)
        if (!data.ok) throw new Error(data.error || "Couldn't answer just now")
        data = data.output
      }
      if (!this.element.isConnected || this._twinChatId !== requestedThreadId || this._personThreadUrl) return
      this._twinChatId = String(data.conversation_id)
      if (!this._selectedRow()) {
        const url = new URL(this.chatUrlValue, window.location.href)
        url.searchParams.set("conversation_id", this._twinChatId)
        this._twinChatUrl = url.toString()
        this._addTwinChatRow({ id: this._twinChatId, title: body })
      }
      this._chatLine("twin", data.answer)
      this.sendStatusTarget.textContent = data.needs_owner ? `Passed to ${this.ownerNameValue}. You'll hear back.` : ""
      this._updateTwinChatRow(body)
    } catch (error) {
      console.error(error)
      this.sendStatusTarget.textContent = error.message || "That didn't go through. Try again."
    } finally {
      this._sendingChat = false
      this.sendButtonTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  async submitPersonMessage(event) {
    const form = event.target.closest("form")
    if (event.defaultPrevented || !form || !this._personThreadUrl || !this.threadTarget.contains(form)) return
    event.preventDefault()

    try {
      const response = await fetch(form.action, {
        method: (form.method || "post").toUpperCase(),
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
          Accept: "application/json, text/html"
        },
        body: new FormData(form),
        credentials: "same-origin"
      })
      const responseBody = await response.text()
      if (!response.ok) throw new Error(`Message send failed (${response.status})`)
      if (responseBody.startsWith("{")) {
        const payload = JSON.parse(responseBody)
        if (payload.ok === false) throw new Error(payload.error || "Message could not be sent.")
      }
      await this.openPersonThread({ preventDefault() {}, currentTarget: this.element.querySelector(".profile-message-row.is-selected") })
    } catch (error) {
      console.error(error)
      this.statusTarget.textContent = "Couldn't send that message. Please try again."
    }
  }

  async _loadTwinChat() {
    const response = await fetch(this._twinChatUrl, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
    if (!response.ok) throw new Error(`Chat history request failed (${response.status})`)
    const data = await response.json()
    if (!data.messages.length) {
      this._showStarters()
      return
    }
    this.threadTarget.replaceChildren()
    const thread = document.createElement("ol")
    thread.className = "assistant-chat profile-chat-thread profile-home-chat-thread profile-chat-thread--log"
    thread.setAttribute("aria-live", "polite")
    thread.setAttribute("aria-label", `Conversation with ${this.ownerNameValue}`)
    data.messages.forEach((message) => {
      const body = message.role === "owner" ? `${data.owner || this.ownerNameValue}: ${message.body}` : message.body
      this._appendChatLine(thread, message.role, body)
    })
    this.threadTarget.appendChild(thread)
    const title = data.conversations.find((chat) => String(chat.id) === this._twinChatId)?.title || "Conversation"
    const row = this._selectedRow()
    if (row) {
      row.dataset.threadTitle = title
      const label = row.querySelector(".messages-recent-name")
      if (label) label.textContent = title
    }
    this._setTitle(title)
    this._scrollToEnd()
  }

  _openChatPanel() {
    this._chatPanel()?.open()
  }

  _chatPanel() {
    return this.application.getControllerForElementAndIdentifier(this.element, "profile-chat-panel")
  }

  _setTitle(title) {
    this._chatTitle = title
    this.titleTarget.textContent = this.hasHistoryTarget && !this.historyTarget.hidden ? "Chat history" : title
  }

  _openSidebar() {
    this._openChatPanel()
    this._chatOpen = true
    this.sidebarTarget.hidden = false
    if (this.hasHistoryTarget) this.historyTarget.hidden = true
    if (this.hasHistoryToggleTarget) this.historyToggleTarget.setAttribute("aria-expanded", "false")
  }

  _activate(row) {
    this._openSidebar()
    this.element.querySelectorAll(".profile-message-row.is-selected").forEach((item) => item.classList.remove("is-selected"))
    row.classList.add("is-selected")
  }

  _showLoading(text) {
    this.threadTarget.replaceChildren()
    const status = document.createElement("p")
    status.className = "messages-drawer-loading"
    status.setAttribute("role", "status")
    status.textContent = text
    this.threadTarget.appendChild(status)
  }

  _showEmptyState() {
    this._openSidebar()
    this._personThreadUrl = null
    this._twinChatId = null
    this.twinComposerTarget.classList.toggle("hidden", !this.canChatValue)
    // The person, as a messenger's header shows them; a conversation's own
    // title replaces it once one is open.
    this._setTitle(this.ownerNameValue)
    this._showStarters()
  }

  _showStarters() {
    this.threadTarget.replaceChildren(this.emptyTarget.content.cloneNode(true))
  }

  choosePrompt(event) {
    event.preventDefault()
    const input = this._personThreadUrl ? this.threadTarget.querySelector("textarea.composer-field") : (this.canChatValue && this.inputTarget)
    if (!input) return
    input.value = event.currentTarget.dataset.prompt
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.focus()
  }

  _chatLine(role, body) {
    let thread = this.threadTarget.querySelector(".profile-chat-thread")
    if (!thread) {
      thread = document.createElement("ol")
      thread.className = "assistant-chat profile-chat-thread profile-home-chat-thread profile-chat-thread--log"
      thread.setAttribute("aria-live", "polite")
      thread.setAttribute("aria-label", `Conversation with ${this.ownerNameValue}`)
      this.threadTarget.replaceChildren(thread)
    }
    if (thread) this._appendChatLine(thread, role, body)
    this._scrollToEnd()
  }

  // The Jane chat's bubbles (assistants/_turns): yours on the right, the
  // twin's - and the owner's own replies - on the left.
  _appendChatLine(thread, role, body) {
    const line = document.createElement("li")
    line.className = `assistant-bubble ${role === "asker" ? "assistant-bubble-user" : "assistant-bubble-assistant"}`
    line.dataset.role = role
    line.textContent = body
    thread.appendChild(line)
  }

  _addTwinChatRow(chat) {
    if (!this.hasListTarget) return
    const item = document.createElement("li")
    const button = document.createElement("button")
    button.type = "button"
    button.className = "assistant-thread-item contact-row msg-row--dm profile-message-row is-selected"
    button.dataset.action = "profile-messages#openTwinChat"
    button.dataset.chatId = String(chat.id)
    button.dataset.chatUrl = this._twinChatUrl
    button.dataset.threadTitle = chat.title || "New conversation"
    const avatar = document.createElement("span")
    avatar.className = "messages-avatar messages-avatar-initials"
    avatar.textContent = this.ownerNameValue.charAt(0).toUpperCase()
    const content = document.createElement("span")
    content.className = "assistant-thread-copy"
    const top = document.createElement("span")
    top.className = "assistant-thread-title"
    const name = document.createElement("span")
    name.className = "messages-recent-name"
    name.textContent = button.dataset.threadTitle
    const preview = document.createElement("span")
    preview.className = "messages-recent-snippet"
    preview.textContent = "New conversation"
    top.appendChild(name)
    content.append(top, preview)
    button.append(avatar, content)
    item.appendChild(button)
    this.listTarget.prepend(item)
    this.element.querySelector(".profile-messages-empty-list")?.remove()
    this.element.querySelectorAll(".profile-message-row.is-selected").forEach((row) => {
      if (row !== button) row.classList.remove("is-selected")
    })
  }

  _updateTwinChatRow(body) {
    const row = this._selectedRow()
    if (!row) return
    const snippet = row.querySelector(".messages-recent-snippet")
    if (snippet) snippet.textContent = body
    const title = body.replace(/\s+/g, " ").trim().slice(0, 54)
    row.dataset.threadTitle = title
    const name = row.querySelector(".messages-recent-name")
    if (name) name.textContent = title
    this._setTitle(title)
    if (this._twinChatId) row.dataset.chatId = this._twinChatId
  }

  _selectedRow() {
    return this.element.querySelector(".profile-message-row.is-selected")
  }

  _scrollToEnd() {
    this.threadTarget.querySelectorAll(".messages-thread-scroll, .profile-chat-thread").forEach((element) => {
      element.scrollTop = element.scrollHeight
    })
  }
}
